import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Download, Users, Home, Calendar, Mail, Megaphone, Vote, MessageSquare, QrCode as QrIcon, Save, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, API, qrUrl } from "@/lib/api";
import { QRCode } from "@/components/QRCode";
import { AdminResults } from "@/components/AdminResults";

const KINDS = [
  { key: "volunteer", label: "Volunteers", icon: Users },
  { key: "yard_sign", label: "Yard Signs", icon: Home },
  { key: "meet_greet", label: "Meet & Greet", icon: Calendar },
  { key: "contact", label: "Contact Messages", icon: Mail },
  { key: "newsletter", label: "Newsletter", icon: Megaphone },
  { key: "vote_plan", label: "Vote Plans", icon: Vote },
];

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "submissions", label: "Submissions", icon: Users },
  { key: "chat", label: "Chat Logs", icon: MessageSquare },
  { key: "content", label: "Content", icon: Save },
  { key: "qr", label: "QR Library", icon: QrIcon },
  { key: "results", label: "Election Results", icon: Vote },
];

export default function AdminDashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [kind, setKind] = useState("volunteer");
  const [rows, setRows] = useState([]);
  const [logs, setLogs] = useState([]);
  const [content, setContent] = useState(null);

  const logout = () => { localStorage.removeItem("satish_admin_token"); nav("/admin"); };

  const guard = useCallback((err) => {
    if (err.response?.status === 401) { logout(); }
  }, []); // eslint-disable-line

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(guard);
    api.get("/content").then((r) => setContent(r.data));
  }, [guard]);

  useEffect(() => {
    if (tab === "submissions") api.get(`/admin/submissions?kind=${kind}`).then((r) => setRows(r.data)).catch(guard);
    if (tab === "chat") api.get("/admin/chat-logs").then((r) => setLogs(r.data)).catch(guard);
  }, [tab, kind, guard]);

  const exportCsv = async () => {
    const token = localStorage.getItem("satish_admin_token");
    const res = await fetch(`${API}/admin/export?kind=${kind}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}_submissions.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const saveContent = async () => {
    try {
      await api.put("/admin/content", content);
      toast.success("Content saved. Changes are live.");
    } catch (e) { guard(e); toast.error("Failed to save."); }
  };

  const setPath = (section, key, value) =>
    setContent((c) => ({ ...c, [section]: { ...c[section], [key]: value } }));

  const origin = window.location.origin;
  const qrItems = [
    { data: `${origin}/donate`, label: "Donate", file: "satish-donate" },
    { data: `${origin}/get-involved`, label: "Volunteer", file: "satish-volunteer" },
    { data: `${origin}/vote`, label: "Vote", file: "satish-vote" },
    { data: origin, label: "Campaign Home", file: "satish-home" },
  ];
  const [customUrl, setCustomUrl] = useState("");

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col lg:flex-row">
      {/* SIDEBAR */}
      <aside className="lg:w-64 bg-[#0A192F] text-white lg:min-h-screen p-5" data-testid="admin-sidebar">
        <div className="font-serif font-bold text-lg mb-6 flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-brightblue flex items-center justify-center text-sm">SC</div> Admin</div>
        <nav className="flex lg:flex-col gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.key ? "bg-brightblue text-white" : "text-blue-200 hover:bg-white/10"}`}
              data-testid={`admin-tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <Button onClick={logout} variant="outline" className="mt-6 w-full bg-transparent border-white/20 text-white hover:bg-white/10 hover:text-white" data-testid="admin-logout">
          <LogOut className="w-4 h-4 mr-2" /> Log Out
        </Button>
      </aside>

      {/* MAIN */}
      <main className="flex-1 p-5 sm:p-8">
        {/* OVERVIEW */}
        {tab === "overview" && (
          <div data-testid="admin-overview">
            <h1 className="font-serif text-2xl font-bold text-navy mb-6">Dashboard Overview</h1>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {KINDS.map((k) => (
                <div key={k.key} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid={`stat-${k.key}`}>
                  <k.icon className="w-6 h-6 text-brightblue" />
                  <div className="text-3xl font-bold text-navy mt-3">{stats?.[k.key] ?? "—"}</div>
                  <div className="text-sm text-slate-500">{k.label}</div>
                </div>
              ))}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="stat-chat">
                <MessageSquare className="w-6 h-6 text-brightblue" />
                <div className="text-3xl font-bold text-navy mt-3">{stats?.chat_questions ?? "—"}</div>
                <div className="text-sm text-slate-500">Chat Questions</div>
              </div>
            </div>
          </div>
        )}

        {/* SUBMISSIONS */}
        {tab === "submissions" && (
          <div data-testid="admin-submissions">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <h1 className="font-serif text-2xl font-bold text-navy">Submissions</h1>
              <Button onClick={exportCsv} className="bg-brightblue hover:bg-brightblue-hover text-white" data-testid="export-csv-btn"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
            </div>
            <div className="flex flex-wrap gap-2 mb-4">
              {KINDS.map((k) => (
                <button key={k.key} onClick={() => setKind(k.key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-semibold ${kind === k.key ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`}
                  data-testid={`kind-${k.key}`}>{k.label}</button>
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
              {rows.length === 0 ? (
                <p className="p-8 text-center text-slate-400">No submissions yet.</p>
              ) : (
                <table className="w-full text-sm" data-testid="submissions-table">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      {Object.keys(rows[0]).filter((k) => !["id", "kind"].includes(k)).map((h) => (
                        <th key={h} className="px-4 py-3 font-semibold text-slate-600 capitalize whitespace-nowrap">{h.replace(/_/g, " ")}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="border-t border-slate-100">
                        {Object.keys(rows[0]).filter((k) => !["id", "kind"].includes(k)).map((h) => (
                          <td key={h} className="px-4 py-3 text-slate-700 max-w-[260px] truncate">{Array.isArray(r[h]) ? r[h].join(", ") : String(r[h] ?? "")}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* CHAT LOGS */}
        {tab === "chat" && (
          <div data-testid="admin-chat-logs">
            <h1 className="font-serif text-2xl font-bold text-navy mb-5">Chatbot Question Logs</h1>
            <div className="space-y-3">
              {logs.length === 0 ? <p className="text-slate-400">No chat logs yet.</p> : logs.map((l) => (
                <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                  <div className="font-semibold text-navy">Q: {l.question}</div>
                  <div className="text-sm text-slate-600 mt-1">A: {l.answer}</div>
                  <div className="text-xs text-slate-400 mt-2">{new Date(l.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTENT */}
        {tab === "content" && content && (
          <div data-testid="admin-content" className="max-w-3xl">
            <div className="flex items-center justify-between mb-5">
              <h1 className="font-serif text-2xl font-bold text-navy">Edit Site Content</h1>
              <Button onClick={saveContent} className="bg-brightblue hover:bg-brightblue-hover text-white" data-testid="save-content-btn"><Save className="w-4 h-4 mr-2" /> Save</Button>
            </div>
            <div className="space-y-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Tagline</span>
                <Input value={content.tagline || ""} onChange={(e) => setContent({ ...content, tagline: e.target.value })} data-testid="content-tagline" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Hero Headline</span>
                <Input value={content.home?.hero_headline || ""} onChange={(e) => setPath("home", "hero_headline", e.target.value)} data-testid="content-hero-headline" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Hero Subhead</span>
                <Textarea value={content.home?.hero_subhead || ""} onChange={(e) => setPath("home", "hero_subhead", e.target.value)} data-testid="content-hero-subhead" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Intro (short)</span>
                <Textarea value={content.home?.intro_short || ""} onChange={(e) => setPath("home", "intro_short", e.target.value)} data-testid="content-intro-short" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Intro (full)</span>
                <Textarea rows={5} value={content.home?.intro_full || ""} onChange={(e) => setPath("home", "intro_full", e.target.value)} data-testid="content-intro-full" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Biography</span>
                <Textarea rows={5} value={content.about?.bio || ""} onChange={(e) => setPath("about", "bio", e.target.value)} data-testid="content-bio" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Why I'm Running</span>
                <Textarea rows={4} value={content.about?.why_running || ""} onChange={(e) => setPath("about", "why_running", e.target.value)} data-testid="content-why" /></label>
              <label className="block"><span className="text-sm font-semibold text-slate-700 mb-1.5 block">Chatbot Knowledge Base <span className="text-slate-400 font-normal">(what "Ask Team Satish" can answer from)</span></span>
                <Textarea rows={7} value={content.knowledge_base || ""} onChange={(e) => setContent({ ...content, knowledge_base: e.target.value })} data-testid="content-kb" /></label>
            </div>
          </div>
        )}

        {tab === "results" && <AdminResults guard={guard} />}

        {/* QR LIBRARY */}
        {tab === "qr" && (
          <div data-testid="admin-qr">
            <h1 className="font-serif text-2xl font-bold text-navy mb-5">QR Code Library</h1>
            <div className="flex flex-wrap gap-6 mb-10">
              {qrItems.map((q) => (
                <QRCode key={q.file} data={q.data} label={q.label} filename={q.file} size={160} />
              ))}
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 max-w-xl">
              <h2 className="font-serif font-bold text-navy mb-3">Create a QR Code from any URL</h2>
              <div className="flex gap-2 mb-4">
                <Input placeholder="https://..." value={customUrl} onChange={(e) => setCustomUrl(e.target.value)} data-testid="qr-custom-url" />
              </div>
              {customUrl && (
                <div className="flex justify-center">
                  <QRCode data={customUrl} label="Custom QR" filename="satish-custom" size={160} />
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
